import React, { useState } from 'react';
import {
	autoUpdate,
	flip,
	FloatingPortal,
	offset,
	shift,
	useFloating,
	useHover,
	useInteractions
} from '@floating-ui/react';
import { applicantStore } from 'ats/pages/applicants/ApplicantStore';
import { jobApplicationStore } from 'ats/pages/pipeline/components/JobApplicationStore';
import { getApplicantTaskTheme } from 'ats/utils/applicantTaskTheme';
import { getJobApplicationTaskTheme } from 'ats/utils/jobApplicationTaskTheme';

const STATUS_LABELS = {
	pending: 'Pending',
	cancelled: 'Cancelled',
	completed: 'Completed'
};

function normalizeLatestTask(latestTask) {
	if (!latestTask) {
		return null;
	}

	if (typeof latestTask === 'string') {
		try {
			return JSON.parse(latestTask);
		} catch {
			return null;
		}
	}

	return latestTask;
}

function ChipTooltip({
	label,
	statusLabel,
	jobTitle,
	description,
	status,
	theme,
	children
}) {
	const [isOpen, setIsOpen] = useState(false);
	const comment = String(description ?? '').trim();
	const jobName = String(jobTitle ?? '').trim();

	const { refs, floatingStyles, context } = useFloating({
		open: isOpen,
		onOpenChange: setIsOpen,
		placement: 'bottom-end',
		middleware: [offset(8), flip(), shift({ padding: 8 })],
		whileElementsMounted: autoUpdate
	});

	const hover = useHover(context, {
		delay: { open: 80, close: 80 }
	});
	const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

	return (
		<>
			<div
				ref={refs.setReference}
				className="latest-task-cell"
				style={{
					'--latest-task-accent': theme.accent,
					'--latest-task-bg': theme.cardBg,
					'--latest-task-border': theme.accent
				}}
				aria-label={`${label}, ${statusLabel}`}
				{...getReferenceProps()}>
				{children}
				<span
					className={`latest-task-cell__status-dot is-${status}`}
					aria-hidden="true"
				/>
			</div>
			{isOpen && (
				<FloatingPortal>
					<div
						ref={refs.setFloating}
						style={floatingStyles}
						className="latest-task-cell__tooltip"
						role="tooltip"
						{...getFloatingProps()}>
						<div className="latest-task-cell__tooltip-header">
							<div className="latest-task-cell__tooltip-status">
								<span className="latest-task-cell__tooltip-status-label">
									STATUS:
								</span>
								<span className="latest-task-cell__tooltip-status-value">
									{statusLabel}
								</span>
							</div>
							{jobName ? (
								<div className="latest-task-cell__tooltip-job">{jobName}</div>
							) : null}
						</div>
						{comment ? (
							<>
								<div className="latest-task-cell__tooltip-divider" />
								<div className="latest-task-cell__tooltip-description">
									{comment}
								</div>
							</>
						) : null}
					</div>
				</FloatingPortal>
			)}
		</>
	);
}

export default function LatestTaskCell({ latestTask, showJobTitle = true }) {
	const task = normalizeLatestTask(latestTask);

	if (!task?.type) {
		return <span className="latest-task-cell latest-task-cell--empty">-</span>;
	}

	const isApplicantTask = task.source === 'applicant';
	const taskOption = isApplicantTask
		? applicantStore.getTaskByValue(task.type)
		: jobApplicationStore.getTaskByValue(task.type);
	const theme = isApplicantTask
		? getApplicantTaskTheme(task.type)
		: getJobApplicationTaskTheme(task.type);
	const label = taskOption?.label || task.type;
	const jobTitle = showJobTitle && !isApplicantTask ? task.jobTitle : null;
	const statusLabel = STATUS_LABELS[task.status] || task.status;

	return (
		<ChipTooltip
			label={label}
			status={task.status}
			statusLabel={statusLabel}
			jobTitle={jobTitle}
			description={task.description}
			theme={theme}>
			<div className="latest-task-cell__type">
				<img src={theme.icon} alt="" />
				<span className="latest-task-cell__label-wrap">
					<span className="latest-task-cell__label">{label}</span>
				</span>
			</div>
		</ChipTooltip>
	);
}
